import {
  Bold,
  Italic,
  Underline,
  Strikethrough,
  Code,
  Highlighter,
  Link2,
  List,
  ListOrdered,
  ListChecks,
  Quote,
  Code2,
  Undo2,
  Redo2,
  Heading1,
  Heading2,
  Heading3,
  Pilcrow,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  Subscript as SubscriptIcon,
  Superscript as SuperscriptIcon,
} from 'lucide-react';
import { cn } from '../lib/utils';

function ToolbarButton({ onClick, active, disabled, title, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      aria-label={title}
      className={cn(
        'rounded-md p-1.5 text-slate-600 transition-colors disabled:opacity-40',
        active ? 'bg-slate-200 text-slate-900' : 'hover:bg-slate-100'
      )}
    >
      {children}
    </button>
  );
}

function Divider() {
  return <span className="mx-1 h-5 w-px bg-slate-200" aria-hidden="true" />;
}

const ICON_SIZE = 'h-4 w-4';

export default function EditorToolbar({ editor }) {
  if (!editor) return null;

  const setLink = () => {
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('Link URL', previousUrl || 'https://');
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  const buttons = {
    undo: { icon: Undo2, title: 'Undo', action: (e) => e.chain().focus().undo().run() },
    redo: { icon: Redo2, title: 'Redo', action: (e) => e.chain().focus().redo().run() },
    paragraph: { icon: Pilcrow, title: 'Paragraph', active: () => !editor.isActive('heading'), action: (e) => e.chain().focus().setParagraph().run() },
    h1: { icon: Heading1, title: 'Heading 1', active: () => editor.isActive('heading', { level: 1 }), action: (e) => e.chain().focus().toggleHeading({ level: 1 }).run() },
    h2: { icon: Heading2, title: 'Heading 2', active: () => editor.isActive('heading', { level: 2 }), action: (e) => e.chain().focus().toggleHeading({ level: 2 }).run() },
    h3: { icon: Heading3, title: 'Heading 3', active: () => editor.isActive('heading', { level: 3 }), action: (e) => e.chain().focus().toggleHeading({ level: 3 }).run() },
    bold: { icon: Bold, title: 'Bold', active: () => editor.isActive('bold'), action: (e) => e.chain().focus().toggleBold().run() },
    italic: { icon: Italic, title: 'Italic', active: () => editor.isActive('italic'), action: (e) => e.chain().focus().toggleItalic().run() },
    underline: { icon: Underline, title: 'Underline', active: () => editor.isActive('underline'), action: (e) => e.chain().focus().toggleUnderline().run() },
    strike: { icon: Strikethrough, title: 'Strikethrough', active: () => editor.isActive('strike'), action: (e) => e.chain().focus().toggleStrike().run() },
    code: { icon: Code, title: 'Inline code', active: () => editor.isActive('code'), action: (e) => e.chain().focus().toggleCode().run() },
    highlight: { icon: Highlighter, title: 'Highlight', active: () => editor.isActive('highlight'), action: (e) => e.chain().focus().toggleHighlight().run() },
    link: { icon: Link2, title: 'Link', active: () => editor.isActive('link'), action: () => setLink() },
    superscript: { icon: SuperscriptIcon, title: 'Superscript', active: () => editor.isActive('superscript'), action: (e) => e.chain().focus().toggleSuperscript().run() },
    subscript: { icon: SubscriptIcon, title: 'Subscript', active: () => editor.isActive('subscript'), action: (e) => e.chain().focus().toggleSubscript().run() },
    bulletList: { icon: List, title: 'Bullet list', active: () => editor.isActive('bulletList'), action: (e) => e.chain().focus().toggleBulletList().run() },
    orderedList: { icon: ListOrdered, title: 'Ordered list', active: () => editor.isActive('orderedList'), action: (e) => e.chain().focus().toggleOrderedList().run() },
    taskList: { icon: ListChecks, title: 'Task list', active: () => editor.isActive('taskList'), action: (e) => e.chain().focus().toggleTaskList().run() },
    blockquote: { icon: Quote, title: 'Blockquote', active: () => editor.isActive('blockquote'), action: (e) => e.chain().focus().toggleBlockquote().run() },
    codeBlock: { icon: Code2, title: 'Code block', active: () => editor.isActive('codeBlock'), action: (e) => e.chain().focus().toggleCodeBlock().run() },
    alignLeft: { icon: AlignLeft, title: 'Align left', active: () => editor.isActive({ textAlign: 'left' }), action: (e) => e.chain().focus().setTextAlign('left').run() },
    alignCenter: { icon: AlignCenter, title: 'Align center', active: () => editor.isActive({ textAlign: 'center' }), action: (e) => e.chain().focus().setTextAlign('center').run() },
    alignRight: { icon: AlignRight, title: 'Align right', active: () => editor.isActive({ textAlign: 'right' }), action: (e) => e.chain().focus().setTextAlign('right').run() },
    justify: { icon: AlignJustify, title: 'Justify', active: () => editor.isActive({ textAlign: 'justify' }), action: (e) => e.chain().focus().setTextAlign('justify').run() },
  };

  const groups = [
    ['undo', 'redo'],
    ['paragraph', 'h1', 'h2', 'h3'],
    ['bold', 'italic', 'underline', 'strike', 'code', 'highlight', 'link', 'superscript', 'subscript'],
    ['bulletList', 'orderedList', 'taskList', 'blockquote', 'codeBlock'],
    ['alignLeft', 'alignCenter', 'alignRight', 'justify'],
  ];

  return (
    <div className="simple-editor__toolbar-inner">
      {groups.map((group, groupIndex) => (
        <div key={groupIndex} className="flex items-center gap-0.5">
          {groupIndex > 0 && <Divider />}
          {group.map((key) => {
            const { icon: Icon, title, active, action } = buttons[key];
            return (
              <ToolbarButton
                key={key}
                title={title}
                active={active?.() ?? false}
                onClick={() => action(editor)}
              >
                <Icon className={ICON_SIZE} />
              </ToolbarButton>
            );
          })}
        </div>
      ))}
    </div>
  );
}
